import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, UserPlus, KeyRound, Check, X, UserCheck, Lock, ShieldCheck, Loader2, FolderOpen, Link2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const empty = { email: "", name: "", password: "", role: "operaio" };
const MASTER_EMAIL = "semasrl22@gmail.com";
const roleLabel = { admin: "AMMINISTRATORE", capocantiere: "CAPOCANTIERE", operaio: "OPERAIO" };
const roleColor = {
  admin: "bg-primary text-primary-foreground",
  capocantiere: "bg-accent text-accent-foreground",
  operaio: "bg-secondary text-secondary-foreground",
};

export default function OperaiPage() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [pwdOpen, setPwdOpen] = useState(null); // user obj
  const [newPwd, setNewPwd] = useState("");
  const [approveFor, setApproveFor] = useState(null); // pending obj
  const [approveRole, setApproveRole] = useState("operaio");
  // Cambio ruolo con PIN
  const [roleChange, setRoleChange] = useState(null); // { user, newRole }
  const [rolePin, setRolePin] = useState("");
  const [roleBusy, setRoleBusy] = useState(false);
  // HR Link Modal
  const [hrLinkOpen, setHrLinkOpen] = useState(null); // user obj
  const [hrDipendenti, setHrDipendenti] = useState([]);
  const [hrSelected, setHrSelected] = useState("");
  const [hrLinkBusy, setHrLinkBusy] = useState(false);

  const load = async () => {
    const [u, p, hd] = await Promise.all([
      api.get("/users"),
      api.get("/users/pending").catch(() => ({ data: [] })),
      api.get("/users/hr-dipendenti").catch(() => ({ data: [] })),
    ]);
    setUsers(u.data); setPending(p.data || []); setHrDipendenti(hd.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/users", form); toast.success("Utente creato"); setForm(empty); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare utente?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Eliminato"); load(); } catch { toast.error("Errore"); }
  };
  const resetPwd = async () => {
    if (!pwdOpen) return;
    if (newPwd.length < 8) { toast.error("Minimo 8 caratteri"); return; }
    try {
      await api.patch(`/users/${pwdOpen.id}/password`, { new_password: newPwd });
      toast.success(`Password di ${pwdOpen.email} reimpostata`);
      setPwdOpen(null); setNewPwd("");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const approve = async () => {
    if (!approveFor) return;
    try {
      await api.post(`/users/pending/${approveFor.id}/approva`, null, { params: { role: approveRole } });
      toast.success(`${approveFor.email} attivato come ${approveRole}`);
      setApproveFor(null); setApproveRole("operaio"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const reject = async (pid) => {
    if (!window.confirm("Rifiutare questa richiesta?")) return;
    try { await api.post(`/users/pending/${pid}/rifiuta`); toast.success("Rifiutata"); load(); } catch { toast.error("Errore"); }
  };

  const confirmRoleChange = async () => {
    if (!roleChange) return;
    if (!rolePin) return toast.error("Inserisci il codice di sicurezza");
    setRoleBusy(true);
    try {
      await api.patch(`/users/${roleChange.user.id}/role`, {
        role: roleChange.newRole,
        admin_password: rolePin,
      });
      toast.success(`Ruolo di ${roleChange.user.email} aggiornato a ${roleLabel[roleChange.newRole]}`);
      setRoleChange(null); setRolePin(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non autorizzata");
    } finally { setRoleBusy(false); }
  };

  const openHrLink = (u) => {
    setHrLinkOpen(u);
    setHrSelected(u.hr_employee_id || "");
  };
  const applyHrLink = async () => {
    if (!hrLinkOpen) return;
    const taken = hrDipendenti.find((d) => d.id === hrSelected && d.user_id && d.user_id !== hrLinkOpen.id);
    if (hrSelected && hrSelected !== "__create__" && taken) {
      if (!window.confirm(`${taken.nome} ${taken.cognome || ""} è già collegato ad un altro utente. Spostare qui il collegamento?`)) return;
    }
    setHrLinkBusy(true);
    try {
      if (hrSelected === "__create__") {
        const { data } = await api.post(`/users/${hrLinkOpen.id}/create-hr-dossier`);
        if (data.reused) toast.success("Collegato alla scheda HR esistente");
        else toast.success("Scheda HR creata e collegata");
      } else {
        await api.post(`/users/${hrLinkOpen.id}/link-hr`, { hr_employee_id: hrSelected || null });
        toast.success(hrSelected ? "Collegamento aggiornato" : "Collegamento rimosso");
      }
      setHrLinkOpen(null); setHrSelected("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore collegamento"); }
    finally { setHrLinkBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Team</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Operai & Team</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-user" className="h-11"><UserPlus className="h-4 w-4 mr-2" /> Aggiungi persona</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuovo utente</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nome</Label><Input data-testid="u-nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Email</Label><Input data-testid="u-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Password temporanea</Label><Input data-testid="u-pwd" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div><Label>Ruolo</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="u-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operaio">Operaio</SelectItem>
                    <SelectItem value="capocantiere">Capocantiere</SelectItem>
                    <SelectItem value="admin">Amministratore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="u-save" onClick={save} className="w-full h-11">Crea utente</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {pending.length > 0 && (
        <Card className="card-elevated border-amber-400">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-amber-600" />
              <div className="font-bold">Richieste di registrazione in attesa ({pending.length})</div>
            </div>
            <p className="text-xs text-muted-foreground">
              Queste persone si sono registrate dalla pagina di login. Scegli il ruolo e approva per attivarle, oppure rifiuta.
            </p>
            <div className="divide-y divide-border">
              {pending.map((p) => (
                <div key={p.id} data-testid={`pending-${p.id}`} className="py-2 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{p.name || p.email}</div>
                    <div className="text-xs text-muted-foreground truncate">{p.email}</div>
                  </div>
                  <Button size="sm" variant="outline" data-testid={`approve-${p.id}`} onClick={() => { setApproveFor(p); setApproveRole("operaio"); }}>
                    <Check className="h-3 w-3 mr-1" /> Approva
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`reject-${p.id}`} onClick={() => reject(p.id)}>
                    <X className="h-3 w-3 mr-1" /> Rifiuta
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="card-elevated">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-secondary">
              <tr className="text-left">
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Nome</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Email</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Ruolo</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMaster = (u.email || "").toLowerCase() === MASTER_EMAIL;
                const isSelf = u.id === currentUser?.id;
                return (
                  <tr key={u.id} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">
                      {u.name}
                      {isMaster && <span className="ml-2 inline-flex items-center gap-1 text-[9px] font-bold text-primary uppercase tracking-widest"><Lock className="h-2.5 w-2.5" />Master</span>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-3">
                      {isMaster ? (
                        <Badge className={`badge-tag ${roleColor[u.role]}`} data-testid={`role-locked-${u.id}`}>
                          <Lock className="h-3 w-3 mr-1" /> {roleLabel[u.role] || u.role}
                        </Badge>
                      ) : (
                        <Select
                          value={u.role}
                          onValueChange={(v) => { if (v !== u.role) { setRoleChange({ user: u, newRole: v }); setRolePin(""); } }}
                          disabled={isSelf}
                        >
                          <SelectTrigger data-testid={`role-select-${u.id}`} className={`h-8 w-44 text-xs font-bold ${roleColor[u.role]} border-0`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">AMMINISTRATORE</SelectItem>
                            <SelectItem value="capocantiere">CAPOCANTIERE</SelectItem>
                            <SelectItem value="operaio">OPERAIO</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right flex gap-1 justify-end">
                      {u.hr_employee_id ? (
                        <Button data-testid={`hr-link-${u.id}`} variant="ghost" size="sm" onClick={() => openHrLink(u)} title="Collegamento HR" className="text-primary">
                          <FolderOpen className="h-3 w-3" />
                        </Button>
                      ) : (
                        <Button data-testid={`hr-link-${u.id}`} variant="ghost" size="sm" onClick={() => openHrLink(u)} title="Collega a scheda HR">
                          <Link2 className="h-3 w-3" />
                        </Button>
                      )}
                      <Button data-testid={`pwd-user-${u.id}`} variant="ghost" size="sm" onClick={() => { setPwdOpen(u); setNewPwd(""); }} title="Reimposta password">
                        <KeyRound className="h-3 w-3" />
                      </Button>
                      {!isMaster && (
                        <Button data-testid={`del-user-${u.id}`} variant="ghost" size="sm" onClick={() => remove(u.id)}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Dialog cambio password */}
      <Dialog open={!!pwdOpen} onOpenChange={(o) => { if (!o) { setPwdOpen(null); setNewPwd(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reimposta password · {pwdOpen?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Imposta una nuova password per questo utente. Comunicagliela a voce o via WhatsApp — da amministratore
              puoi reimpostarla di nuovo in qualsiasi momento.
            </p>
            <div>
              <Label>Nuova password (min 8 caratteri)</Label>
              <Input data-testid="new-pwd-input" type="text" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} />
            </div>
            <Button data-testid="new-pwd-save" onClick={resetPwd} className="w-full h-11" disabled={newPwd.length < 8}>Reimposta</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog approva pending */}
      <Dialog open={!!approveFor} onOpenChange={(o) => { if (!o) setApproveFor(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Approva {approveFor?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Ruolo</Label>
              <Select value={approveRole} onValueChange={setApproveRole}>
                <SelectTrigger data-testid="approve-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operaio">Operaio</SelectItem>
                  <SelectItem value="capocantiere">Capocantiere</SelectItem>
                  <SelectItem value="admin">Amministratore</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="approve-save" onClick={approve} className="w-full h-11">Approva e attiva</Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Dialog conferma cambio ruolo con PIN admin */}
      <Dialog open={!!roleChange} onOpenChange={(o) => { if (!o) { setRoleChange(null); setRolePin(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Conferma cambio ruolo</DialogTitle>
          </DialogHeader>
          {roleChange && (
            <div className="space-y-4">
              <div className="bg-secondary rounded-lg p-3 text-sm space-y-1">
                <div className="font-bold">{roleChange.user.name || roleChange.user.email}</div>
                <div className="text-xs text-muted-foreground">{roleChange.user.email}</div>
                <div className="flex items-center gap-2 mt-2 text-xs">
                  <Badge className={roleColor[roleChange.user.role]}>{roleLabel[roleChange.user.role]}</Badge>
                  <span className="text-muted-foreground">→</span>
                  <Badge className={roleColor[roleChange.newRole]}>{roleLabel[roleChange.newRole]}</Badge>
                </div>
              </div>
              <div>
                <Label>Codice di sicurezza amministratore</Label>
                <p className="text-[11px] text-muted-foreground mb-1">Inserisci la tua password admin per confermare la modifica.</p>
                <Input
                  data-testid="role-pin-input"
                  type="password"
                  autoComplete="current-password"
                  value={rolePin}
                  onChange={(e) => setRolePin(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && confirmRoleChange()}
                  placeholder="••••••••"
                  className="h-11"
                />
              </div>
              <div className="flex gap-2">
                <Button data-testid="role-pin-cancel" variant="outline" className="flex-1 h-11" onClick={() => { setRoleChange(null); setRolePin(""); }}>Annulla</Button>
                <Button data-testid="role-pin-confirm" className="flex-1 h-11" disabled={roleBusy || !rolePin} onClick={confirmRoleChange}>
                  {roleBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                  Conferma
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Dialog collegamento HR */}
      <Dialog open={!!hrLinkOpen} onOpenChange={(o) => { if (!o) { setHrLinkOpen(null); setHrSelected(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="h-5 w-5 text-primary" /> Collegamento HR</DialogTitle>
          </DialogHeader>
          {hrLinkOpen && (
            <div className="space-y-4">
              <div className="bg-secondary rounded-lg p-3 text-sm">
                <div className="font-bold">{hrLinkOpen.name || hrLinkOpen.email}</div>
                <div className="text-xs text-muted-foreground">{hrLinkOpen.email} · {roleLabel[hrLinkOpen.role] || hrLinkOpen.role}</div>
              </div>
              <div>
                <Label>Collega a scheda HR</Label>
                <Select value={hrSelected || "__none__"} onValueChange={(v) => setHrSelected(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="operai-hr-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__create__" data-testid="operai-hr-create">
                      <span className="flex items-center gap-1 text-primary font-semibold"><Sparkles className="h-3 w-3" /> Crea nuova scheda HR da questo utente</span>
                    </SelectItem>
                    <SelectItem value="__none__">— Nessun collegamento —</SelectItem>
                    {hrDipendenti.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.nome} {d.cognome}
                        {d.user_id && d.user_id !== hrLinkOpen.id ? " · già collegato" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-muted-foreground mt-1">La scheda HR potrà essere completata con documenti dall'Archivio HR.</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 h-11" onClick={() => { setHrLinkOpen(null); setHrSelected(""); }}>Annulla</Button>
                <Button data-testid="operai-hr-apply" className="flex-1 h-11" disabled={hrLinkBusy} onClick={applyHrLink}>
                  {hrLinkBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                  Applica
                </Button>
              </div>
              {hrLinkOpen.hr_employee_id && (
                <Button data-testid="operai-hr-open" variant="ghost" size="sm" className="w-full" onClick={() => navigate("/archivio-hr")}>
                  <FolderOpen className="h-3 w-3 mr-1" /> Apri Archivio HR
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
